import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { NbTabsetModule, NbThemeModule } from '@nebular/theme';
import * as echarts from 'echarts';

interface SalesData {
  date: string;
  sales: number;
}

export type DisplayMode = 'period' | 'cumulative';
export type ViewOption = 'toggle' | 'graph' | 'total';

@Component({
  selector: 'pl-sales-orders-graphs',
  standalone:true,
  imports:[CommonModule,NbThemeModule,NbTabsetModule],
  templateUrl: './sales-orders-graphs.component.html',
  styleUrls: ['./sales-orders-graphs.component.scss']
})
export class SalesOrdersGraphsComponent implements OnInit, AfterViewInit{
  @Input() viewOption: ViewOption = 'toggle'; // 'toggle', 'graph', or 'total'
  @Input() defaultMode: DisplayMode = 'period'; // 'period' or 'cumulative'

  private weekChart: echarts.ECharts | null = null;
  private monthChart: echarts.ECharts | null = null;
  private ytdChart: echarts.ECharts | null = null;

  private _weekTotal: number | null = null;
  private _monthTotal: number | null = null;
  private _ytdTotal: number | null = null;

  showCumulative: boolean = false;

  ngOnInit() {
    // Set initial mode based on inputs
    if (this.viewOption === 'total') {
      this.showCumulative = true;
    } else if (this.viewOption === 'graph') {
      this.showCumulative = false;
    } else {
      this.showCumulative = this.defaultMode === 'cumulative';
    }
  }

  ngAfterViewInit() {
    // Small delay to ensure DOM elements are ready
    setTimeout(() => {
      this.initializeCharts();
    }, 100);
  }

  onToggleChange() {
    // Refresh all charts when toggle changes
    if (this.shouldShowChart()) {
      this.initializeCharts();
    }
  }

  shouldShowChart(): boolean {
    return this.viewOption === 'total' || this.viewOption === 'graph';
  }

  shouldShowTotals(): boolean {
    return true
    //return this.viewOption === 'total';
  }

  getModeLabel(): string {
    if (this.viewOption === 'graph') {
      return this.showCumulative ? 'Graph View - Cumulative' : 'Graph View - Period';
    } else if (this.viewOption === 'total') {
      return 'Totals View';
    }
    return '';
  }

  private initializeCharts() {
    this.initWeekChart();
    this.initMonthChart();
    this.initYTDChart();
  }

  private initWeekChart() {
    const chartDom = document.getElementById('weekChart');
    if (!chartDom) return;

    this.weekChart = echarts.init(chartDom);
    const weekData = this.generateWeekData();
    const displayData = this.showCumulative ? this.calculateCumulative(weekData) : weekData;

    const option = {
      title: {
        text: this.showCumulative ? 'Cumulative Weekly Sales' : 'Weekly Sales Trend',
        left: 'center',
        textStyle: {
          fontSize: 16,
          color: '#333'
        }
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0];
          const prefix = this.showCumulative ? 'Cumulative Sales' : 'Sales';
          return `${data.name}<br/>${prefix}: ${data.value.toLocaleString()}`;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: displayData.map(d => d.date),
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '${value}'
        }
      },
      series: [{
        name: 'Sales',
        type: 'line',
        smooth: true,
        data: displayData.map(d => d.sales),
        lineStyle: {
          color: '#5B9BD5',
          width: 3
        },
        itemStyle: {
          color: '#5B9BD5'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(91, 155, 213, 0.3)' },
            { offset: 1, color: 'rgba(91, 155, 213, 0.1)' }
          ])
        }
      }]
    };

    this.weekChart.setOption(option);
  }

  private initMonthChart() {
    const chartDom = document.getElementById('monthChart');
    if (!chartDom) return;

    this.monthChart = echarts.init(chartDom);
    const monthData = this.generateMonthData();
    const displayData = this.showCumulative ? this.calculateCumulative(monthData) : monthData;

    const option = {
      title: {
        text: this.showCumulative ? 'Cumulative Monthly Sales' : 'Monthly Sales Trend',
        left: 'center',
        textStyle: {
          fontSize: 16,
          color: '#333'
        }
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0];
          const prefix = this.showCumulative ? 'Cumulative Sales' : 'Sales';
          return `${data.name}<br/>${prefix}: ${data.value.toLocaleString()}`;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: displayData.map(d => d.date)
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '${value}'
        }
      },
      series: [{
        name: 'Sales',
        type: 'line',
        smooth: true,
        data: displayData.map(d => d.sales),
        lineStyle: {
          color: '#70AD47',
          width: 3
        },
        itemStyle: {
          color: '#70AD47'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(112, 173, 71, 0.3)' },
            { offset: 1, color: 'rgba(112, 173, 71, 0.1)' }
          ])
        }
      }]
    };

    this.monthChart.setOption(option);
  }

  private initYTDChart() {
    const chartDom = document.getElementById('ytdChart');
    if (!chartDom) return;

    this.ytdChart = echarts.init(chartDom);
    const ytdData = this.generateYTDData();
    const displayData = this.showCumulative ? this.calculateCumulative(ytdData) : ytdData;

    const option = {
      title: {
        text: this.showCumulative ? 'Cumulative YTD Sales' : 'Year to Date Sales Trend',
        left: 'center',
        textStyle: {
          fontSize: 16,
          color: '#333'
        }
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0];
          const prefix = this.showCumulative ? 'Cumulative Sales' : 'Sales';
          return `${data.name}<br/>${prefix}: ${data.value.toLocaleString()}`;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: displayData.map(d => d.date),
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '${value}'
        }
      },
      series: [{
        name: 'Sales',
        type: 'line',
        smooth: true,
        data: displayData.map(d => d.sales),
        lineStyle: {
          color: '#FFC000',
          width: 3
        },
        itemStyle: {
          color: '#FFC000'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(255, 192, 0, 0.3)' },
            { offset: 1, color: 'rgba(255, 192, 0, 0.1)' }
          ])
        }
      }]
    };

    this.ytdChart.setOption(option);
  }

  private generateWeekData(): SalesData[] {
    const data: SalesData[] = [];
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      // Generate realistic sales data with weekday patterns
      let baseSales = 15000;
      const dayOfWeek = date.getDay();
      
      // Weekend typically has lower sales
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        baseSales *= 0.7;
      }
      // Mid-week peak
      if (dayOfWeek >= 2 && dayOfWeek <= 4) {
        baseSales *= 1.2;
      }
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 5000);
      
      data.push({
        date: `${dayName} ${monthDay}`,
        sales: sales
      });
    }

    return data;
  }

  private calculateCumulative(data: SalesData[]): SalesData[] {
    let cumulativeTotal = 0;
    return data.map(item => {
      cumulativeTotal += item.sales;
      return {
        date: item.date,
        sales: cumulativeTotal
      };
    });
  }

  private generateMonthData(): SalesData[] {
    const data: SalesData[] = [];
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      // Generate sales with monthly trends
      const dayOfMonth = date.getDate();
      let baseSales = 18000;
      
      // Month-end spike
      if (dayOfMonth >= 25) {
        baseSales *= 1.3;
      }
      // Mid-month dip
      if (dayOfMonth >= 10 && dayOfMonth <= 15) {
        baseSales *= 0.9;
      }
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 8000);
      
      data.push({
        date: dayStr,
        sales: sales
      });
    }

    return data;
  }

  private generateYTDData(): SalesData[] {
    const data: SalesData[] = [];
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    
    // Generate data from January 1st to current month
    for (let month = 0; month <= currentMonth; month++) {
      const daysInMonth = new Date(currentYear, month + 1, 0).getDate();
      
      // For current month, only go up to today
      const maxDay = month === currentMonth ? today.getDate() : daysInMonth;
      
      // Sample every few days to keep chart readable
      const sampleInterval = Math.max(1, Math.floor(maxDay / 10));
      
      for (let day = 1; day <= maxDay; day += sampleInterval) {
        const date = new Date(currentYear, month, day);
        const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        
        // Generate sales with seasonal trends
        let baseSales = 20000;
        
        // Seasonal adjustments
        if (month >= 10 || month <= 1) { // Holiday season
          baseSales *= 1.4;
        } else if (month >= 5 && month <= 7) { // Summer
          baseSales *= 1.1;
        } else if (month >= 2 && month <= 4) { // Spring
          baseSales *= 0.9;
        }
        
        const sales = Math.round(baseSales + (Math.random() - 0.5) * 10000);
        
        data.push({
          date: dateStr,
          sales: sales
        });
      }
    }

    return data;
  }

  // Helper methods for template
  getCurrentWeekRange(): string {
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${monday.toLocaleDateString('en-US', options)} - ${sunday.toLocaleDateString('en-US', options)}`;
  }

  getCurrentMonthName(): string {
    const today = new Date();
    return today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  getCurrentYear(): string {
    return new Date().getFullYear().toString();
  }

  getCurrentMonth(): number {
    return new Date().getMonth();
  }

  get weekTotal(): number {
    if (this._weekTotal === null) {
      this._weekTotal = this.generateWeekData().reduce((sum, item) => sum + item.sales, 0);
    }
    return this._weekTotal;
  }

  /* getWeekTotal(): number {
    return this.generateWeekData().reduce((sum, item) => sum + item.sales, 0);
  } */
  get monthTotal(): number {
    if (this._monthTotal === null) {
      this._monthTotal = this.generateMonthData().reduce((sum, item) => sum + item.sales, 0);
    }
    return this._monthTotal;
  }

  getMonthTotal(): number {
    return this.generateMonthData().reduce((sum, item) => sum + item.sales, 0);
  }

  getMonthAverage(): number {
    const monthData = this.generateMonthData();
    return Math.round(this.getMonthTotal() / monthData.length);
  }

  get ytdTotal(): number {
    if (this._ytdTotal === null) {
      this._ytdTotal = this.generateYTDData().reduce((sum, item) => sum + item.sales, 0);
    }
    return this._ytdTotal;
  }
  getYTDTotal(): number {
    return this.generateYTDData().reduce((sum, item) => sum + item.sales, 0);
  }

  ngOnDestroy() {
    // Clean up charts
    if (this.weekChart) {
      this.weekChart.dispose();
    }
    if (this.monthChart) {
      this.monthChart.dispose();
    }
    if (this.ytdChart) {
      this.ytdChart.dispose();
    }
  }
}
